import { CheckCircle2, Circle, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { TaskChecklistItem } from '../types/task';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';

interface TaskChecklistCardProps {
  taskId: string;
  items: TaskChecklistItem[];
  progress: { total: number; completed: number; percentage: number };
  onToggle: (itemId: string) => Promise<void>;
  onAdd?: (label: string) => Promise<void>;
  onDelete?: (itemId: string) => Promise<void>;
  canEdit?: boolean;
}

const TaskChecklistCard: React.FC<TaskChecklistCardProps> = ({
  taskId,
  items,
  progress,
  onToggle,
  onAdd,
  onDelete,
  canEdit = false,
}) => {
  const [newItemLabel, setNewItemLabel] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const handleAdd = async () => {
    if (!newItemLabel.trim() || !onAdd) return;

    try {
      setIsAdding(true);
      await onAdd(newItemLabel.trim());
      setNewItemLabel('');
    } catch (error) {
      console.error('Error adding checklist item:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggle = async (itemId: string) => {
    try {
      setTogglingId(itemId);
      await onToggle(itemId);
    } catch (error) {
      console.error('Error toggling checklist item:', error);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (itemId: string) => {
    if (!onDelete) return;

    if (!window.confirm('Are you sure you want to delete this checklist item?')) {
      return;
    }

    try {
      await onDelete(itemId);
    } catch (error) {
      console.error('Error deleting checklist item:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Checklist</CardTitle>
          <div className="text-sm font-medium text-muted-foreground">
            {progress.completed} of {progress.total} completed
          </div>
        </div>
        {/* Custom progress bar with green color for completion */}
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700 mt-2">
          <div
            className="h-full bg-green-600 transition-all duration-300 ease-in-out"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No checklist items yet
          </div>
        )}

        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <button
              onClick={() => handleToggle(item.id)}
              disabled={togglingId === item.id}
              className="mt-0.5 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary rounded"
            >
              {item.is_completed ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <Circle className="h-5 w-5 text-muted-foreground" />
              )}
            </button>

            <div className="flex-1 min-w-0">
              <div
                className={`font-medium ${
                  item.is_completed ? 'line-through text-muted-foreground' : 'text-foreground'
                }`}
              >
                {item.label}
                {item.is_required && <span className="text-red-500 ml-1">*</span>}
              </div>

              {item.description && (
                <div className="text-sm text-muted-foreground mt-1">{item.description}</div>
              )}

              {item.order_item && (
                <div className="text-xs text-muted-foreground mt-1">
                  Linked to: {item.order_item.asset_template?.name || 'Order Item'}
                </div>
              )}

              {item.is_completed && item.completed_by_user && (
                <div className="text-xs text-muted-foreground mt-1">
                  Completed by {item.completed_by_user.first_name} {item.completed_by_user.last_name}
                  {item.completed_at && ` on ${new Date(item.completed_at).toLocaleDateString()}`}
                </div>
              )}
            </div>

            {canEdit && onDelete && (
              <button
                onClick={() => handleDelete(item.id)}
                className="flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors focus:outline-none focus:ring-2 focus:ring-destructive rounded p-1"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}

        {canEdit && onAdd && (
          <div className="flex gap-2 pt-2 border-t">
            <Input
              placeholder="Add new checklist item..."
              value={newItemLabel}
              onChange={(e) => setNewItemLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              disabled={isAdding}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleAdd}
              disabled={!newItemLabel.trim() || isAdding}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TaskChecklistCard;

